import { useEffect } from "react"
import { PhotoRecord } from "@/lib/catalog/types"
import { autoSelectNextPhoto } from "@/store/photo/photos-state"

interface UseAutoCorrectSelectionProps {
	currentPhoto: PhotoRecord | null
	currentPhotoClusterIds: string[]
	displayedPhotoIds: string[]
	isShowingClusters: boolean
}

/**
 * Hook that automatically corrects photo selection to maintain valid navigation state.
 *
 * In culling mode with clusters, there are two navigation contexts:
 * 1. Navigating between cluster cover photos (top filmstrip)
 * 2. Navigating within cluster photos (bottom filmstrip)
 *
 * This hook ensures the selected photo is valid for the current navigation context:
 * - When navigating within a cluster: selection must be within the current cluster
 * - When navigating between clusters: selection can be any photo from displayed photos
 *
 * The hook reads navigation state directly via store.get() to avoid unnecessary re-renders
 * while still respecting the current navigation context.
 */
export function useAutoCorrectSelection({
	currentPhoto,
	currentPhotoClusterIds,
	displayedPhotoIds,
	isShowingClusters,
}: UseAutoCorrectSelectionProps) {
	useEffect(() => {
		if (!currentPhoto) {
			return
		}

		// When navigating within cluster, ensure selection stays within cluster
		if (isShowingClusters) {
			if (!currentPhotoClusterIds.includes(currentPhoto.id)) {
				const photosToSearch =
					currentPhotoClusterIds.length > 0 ? currentPhotoClusterIds : displayedPhotoIds
				autoSelectNextPhoto(photosToSearch)
			}

			return
		}

		// When navigating between clusters, allow any photo from displayed photos
		if (!displayedPhotoIds.includes(currentPhoto.id)) {
			autoSelectNextPhoto(displayedPhotoIds)
		}
	}, [currentPhoto, currentPhotoClusterIds, displayedPhotoIds, isShowingClusters])
}
